import { Tool } from '../BaseToolbar.types';
import { ToolOptionsProps } from './tool-options-renderer/ToolOptionsRenderer.types';
import { observer } from 'mobx-react-lite';
import { useStore } from 'src/contexts/StoreContext';
import { StyleProp, View, ViewStyle } from 'react-native';
import { TextIconButton } from '@photosi/rn-components/buttons/text-icon-button/TextIconButton';
import { styles } from './ToolbarOptions.styles';
import { configurationTestID } from 'src/helpers/testIDs.helper';
import { TestIDs } from 'src/constants/testIDs.constants';
import {
  TEXT_ICON_BUTTON_SIZES,
  TEXT_ICON_BUTTON_TYPES,
} from '@photosi/rn-components/buttons/text-icon-button/TextIconButton.constants';
import { ICON_NAMES } from '@photosi/rn-components/icon/icon.helper';
import { FocusGroups } from '@photosi/rn-components/focus-provider/FocusProvider.constants';
import amplitude from 'src/_shared/amplitude';
import { AnalyticsEvents } from 'src/_shared/analyticsEvents';
import { TOOLS_WITH_PRICE } from '../../group-lookup-toolbar/GroupLookupToolbar.constants';
import { LookupCodes } from 'src/constants/lookup.constants';

export type ToolbarOptionsProps = {
  isActive: boolean;
  onBackPress: () => void;
  style?: StyleProp<ViewStyle>;
} & Tool<ToolOptionsProps> &
  Required<Pick<Tool<ToolOptionsProps>, 'ToolOptionsRenderer'>>;

export const ToolbarOptions = observer(
  ({ tool, ToolOptionsRenderer, onBackPress, isActive, onSelectToolOption, style }: ToolbarOptionsProps) => {
    const { i18n } = useStore();

    const enabledChoices = tool.choices.filter(choice => choice.enabled);
    const isPriceVisible =
      enabledChoices.length > 1 &&
      TOOLS_WITH_PRICE.includes(enabledChoices[0]?.productOptionCode as LookupCodes) &&
      enabledChoices.some((choice, _index, choices) => choices[0].price !== choice.price);

    return (
      <View style={[styles.container, style, !isActive && { display: 'none' }]}>
        <TextIconButton
          testID={configurationTestID(tool.code, TestIDs.CLOSE)}
          style={styles.backButton}
          innerStyle={styles.backButtonInner}
          focusStyle={styles.backButtonFocus}
          onPress={async () => {
            onBackPress();
            amplitude.trackEvent(AnalyticsEvents.personalize.menuBackClick, {
              lookup_code: tool.code,
              target: 'lookup_menu',
            });
          }}
          accessible={true}
          accessibilityLabel={i18n.t(`actions:open_x`, { x: i18n.t(tool.titleKey) })}
          accessibilityRole="button"
          text={i18n.t(tool.titleKey)}
          textStyle={styles.backButtontext}
          iconName={ICON_NAMES.ArrowLeft2}
          iconStyle={styles.backButtonIcon}
          size={TEXT_ICON_BUTTON_SIZES.SMALL}
          type={TEXT_ICON_BUTTON_TYPES.ICON_LEFT}
          focusId={configurationTestID(tool.code, TestIDs.CLOSE)}
          focusOrder={0}
          focusGroup={FocusGroups.TOOLBAR_OPTIONS_HEADER}
          focusable={isActive}
        />

        <ToolOptionsRenderer
          options={tool.choices}
          onSelectToolOption={onSelectToolOption}
          isActive={isActive}
          isPriceVisible={isPriceVisible}
        />
      </View>
    );
  },
);

ToolbarOptions.displayName = 'ToolbarOptions';
